/**
 * @tradeview/core — the platform the modules plug into.
 *
 * Owns: the module contract (manifest schema), tenancy path resolution, the module
 * registry, and the entitlement check. Owns nothing a module should own.
 */
import { z } from 'zod';
import { Manifest as ManifestSchema, PhaseStates } from './manifest.schema.js';
import { ROLES, type Role } from '@tradeview/shared';

export { ManifestSchema, PhaseStates };
export { ENTITLEMENTS_DOC, withDefaults, startTrial, setModule } from './entitlements.js';
export type Manifest = z.infer<typeof ManifestSchema>;
export type ModuleState = (typeof PhaseStates)[number];

// ── Tenancy (roadmap §3.4, decision 2): path-based with a legacy resolver ───────
/** The company id that stands for Nextgen's v1 data at the Firestore root. */
export const LEGACY_COMPANY_ID = 'nextgen';

/** Resolve collection paths for a company. Legacy resolves to the root collections v1 uses. */
export function paths(companyId: string) {
  const root = companyId === LEGACY_COMPANY_ID ? '' : `companies/${companyId}/`;
  return {
    companyId,
    isLegacy: companyId === LEGACY_COMPANY_ID,
    company: companyId === LEGACY_COMPANY_ID ? 'settings/companyProfile' : `companies/${companyId}`,
    projects: `${root}projects`,
    project: (pid: string) => `${root}projects/${pid}`,
    projectSub: (pid: string, sub: string) => `${root}projects/${pid}/${sub}`,
    users: `${root}users`,
    timeEntries: `${root}timeEntries`,
    schedule: `${root}schedule`,
    settings: `${root}settings`,
    collection: (name: string) => `${root}${name}`,
  } as const;
}

// ── Entitlements ──────────────────────────────────────────────────────────────
export interface Entitlements {
  /** module id -> state. Missing = locked. */
  modules: Record<string, Exclude<ModuleState, 'empty'> | { state: 'trial'; endsAt: string }>;
}

export function moduleState(ent: Entitlements | null | undefined, id: string): ModuleState {
  const v = ent?.modules?.[id];
  if (!v) return 'locked';
  if (typeof v === 'string') return v;
  return v.state;
}

export function trialDaysLeft(ent: Entitlements | null | undefined, id: string, now = new Date()): number | null {
  const v = ent?.modules?.[id];
  if (!v || typeof v === 'string') return null;
  return Math.max(0, Math.ceil((new Date(v.endsAt).getTime() - now.getTime()) / 86_400_000));
}

// ── Health: what a module says about one job for the job's front door ─────────
export interface HealthTile { label: string; big: string; sub: string; tone?: 'muted' | 'warn' | 'crit'; to: string }
export interface HealthNext { label: string; detail: string; warn: boolean; to: string }
export interface Health { tiles: HealthTile[]; next: HealthNext[] }
/** The module's health function: given the job's id and the company's paths, its tiles and next things. */
export type HealthFn = (ctx: { pid: string; companyId: string; project: (pid: string) => string; projectSub: (pid: string, sub: string) => string }) => Promise<Health>;

// ── Registry ──────────────────────────────────────────────────────────────────
export interface RegisteredModule {
  manifest: Manifest;
  /** Lazy screen loader: export name -> component. Filled by the generated registry. */
  screens: Record<string, () => Promise<{ default: unknown }>>;
  /** Lazy health loader when the manifest names one. Filled by the generated registry. */
  health?: () => Promise<HealthFn>;
}

const registry = new Map<string, RegisteredModule>();

export function registerModule(m: RegisteredModule) {
  const parsed = ManifestSchema.parse(m.manifest);
  for (const r of parsed.roles) if (!(r in ROLES)) throw new Error(`module ${parsed.id}: unknown role ${r}`);
  registry.set(parsed.id, { ...m, manifest: parsed });
}

export function listModules(): RegisteredModule[] {
  return [...registry.values()].sort((a, b) => a.manifest.name.localeCompare(b.manifest.name));
}

export function getModule(id: string): RegisteredModule | undefined { return registry.get(id); }

/** Modules a person can open: role allowed, and the company has it active or on trial. */
export function visibleModules(role: Role | string | null, ent: Entitlements | null): RegisteredModule[] {
  return listModules().filter(m => {
    const st = moduleState(ent, m.manifest.id);
    return (st === 'active' || st === 'trial') && (!role || m.manifest.roles.includes(role));
  });
}

/** Dependencies that are not active/trial for this company, for the "Add X first?" prompt. */
export function missingDependencies(id: string, ent: Entitlements | null): string[] {
  const m = registry.get(id);
  if (!m) return [];
  return m.manifest.dependencies.filter(d => !['active', 'trial'].includes(moduleState(ent, d)));
}

/** A room the shell can stand in: the core's Jobs and Company, plus every room a visible module declares. */
export interface RoomDef { id: string; label: string; door: string; picks?: 'job' | 'bid'; moduleId: string | null }
export const JOB_ROOM: RoomDef = { id: 'job', label: 'Jobs', door: '/job/:id', picks: 'job', moduleId: null };
export const COMPANY_ROOM: RoomDef = { id: 'company', label: 'Company', door: '/company', moduleId: null };
/** The rooms this company has, in the order they are offered: Jobs, then the modules' rooms (Bids first when present), then Company. */
export function roomsFor(role: Role | string | null, ent: Entitlements | null): RoomDef[] {
  const declared = visibleModules(role, ent).flatMap(m => m.manifest.rooms.map(r => ({ ...r, moduleId: m.manifest.id as string | null })));
  declared.sort((a, b) => Number(!!b.picks) - Number(!!a.picks) || a.label.localeCompare(b.label));
  return [JOB_ROOM, ...declared, COMPANY_ROOM];
}

/** The health loaders of every visible module that declares one, in nav order, for the job's front door. */
export function healthFor(role: Role | string | null, ent: Entitlements | null): Array<{ moduleId: string; load: () => Promise<HealthFn> }> {
  return visibleModules(role, ent)
    .filter(m => m.health)
    .sort((a, b) => (a.manifest.nav[0]?.order ?? 999) - (b.manifest.nav[0]?.order ?? 999))
    .map(m => ({ moduleId: m.manifest.id, load: m.health! }));
}

/** Nav entries for one surface, from every visible module, in declared order. */
export function navFor(surface: 'phone' | 'desktop', role: Role | string | null, ent: Entitlements | null) {
  return visibleModules(role, ent)
    .flatMap(m => m.manifest.nav.filter(n => n.where.includes(surface)).map(n => ({ ...n, moduleId: m.manifest.id, bundle: m.manifest.bundle })))
    .sort((a, b) => a.order - b.order);
}
